import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Err, Ok } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { useConnections } from 'app/connections/application'
import { ConnectionConfig, SupportedAccount, isInstalledConnection } from 'app/connections/types'
import { FacebookConnectionConfig } from 'app/connections/types/facebook'
import { GoogleAdwordsConnectionConfig } from 'app/connections/types/googleAdwords'

import { Button } from 'components'

import { GenericSettings, ShopifySettings } from '.'
import { useConnection } from '../components/ConnectionContext'
import InnkeeprSettings from './InnkeeprSettings/InnkeeprSettings'
import styles from './Settings.module.scss'
import { GoogleAdwordsSettings } from './google/AdwordsSettings'
import { MetaSettings } from './meta/Settings'

export interface SettingsProps {
  onConnectionConfigValidationUpdate: (isValidConfig: boolean) => void
}

const noSaveIfInstalled = ['innkeepr']

type SaveButtonState = 'available' | 'disabled' | 'hidden'

const ConnectionSettings = () => {
  const connection = useConnection()
  const { t } = useTranslation('connections')

  const { createNotification, te } = useNotifications()
  const { saveConnection } = useConnections()

  const [connectionConfig, setConnectionConfig] = useState<ConnectionConfig>(connection.config)

  const [saveButtonState, setSaveButtonState] = useState<SaveButtonState>('disabled')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isValidConfig, setIsValidConfig] = useState<boolean>(false)

  useEffect(() => {
    const determineSaveButtonState = () => {
      if (!connection) return 'hidden'
      if (connection.readonly) return 'hidden'

      if (
        isInstalledConnection(connection) &&
        noSaveIfInstalled.includes(connection.name) &&
        connection.status === 'active'
      ) {
        return 'hidden'
      }

      return isValidConfig ? 'available' : 'disabled'
    }

    setSaveButtonState(determineSaveButtonState())
  }, [connection, isValidConfig])

  const handleSave = useCallback(async () => {
    setIsSaving(true)

    return saveConnection(connection, connectionConfig)
      .then((result) => {
        if (result.ok) {
          createNotification({
            message: t(
              `${isInstalledConnection(connection) ? 'update' : 'create'}.success.message`,
              {
                name: t(`available.${connection.name}.displayName`),
              },
            ),
            title: t(`${isInstalledConnection(connection) ? 'update' : 'create'}.success.title`),
            type: 'success',
          })

          return Ok(undefined)
        } else {
          createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          })

          return Err(undefined)
        }
      })
      .finally(() => setIsSaving(false))
  }, [connection, connectionConfig, saveConnection, createNotification, t, te])

  const handleConnectionConfigUpdate = useCallback(
    (key: string, value: SupportedAccount[] | string) =>
      setConnectionConfig((prev) =>
        prev ? ({ ...prev, [key]: value } as ConnectionConfig) : prev,
      ),
    [],
  )

  const handleOnShopUpdate = useCallback(
    (shop: string) => handleConnectionConfigUpdate('shop', shop),
    [handleConnectionConfigUpdate],
  )

  const handleOnTrackingUrlUpdate = useCallback(
    (trackingUrl: string) => handleConnectionConfigUpdate('trackingUrl', trackingUrl),
    [handleConnectionConfigUpdate],
  )

  const renderConnectionSettings = () => {
    switch (connection.name) {
      case 'facebook':
        return (
          <MetaSettings
            connection={connection}
            connectionConfig={connectionConfig as FacebookConnectionConfig}
            onConnectionConfigUpdate={handleConnectionConfigUpdate}
            onConnectionConfigValidationUpdate={setIsValidConfig}
          />
        )
      case 'googleAdwords':
        return (
          <GoogleAdwordsSettings
            connection={connection}
            connectionConfig={connectionConfig as GoogleAdwordsConnectionConfig}
            onConnectionConfigUpdate={handleConnectionConfigUpdate}
            onConnectionConfigValidationUpdate={setIsValidConfig}
          />
        )
      case 'innkeepr':
        return (
          <InnkeeprSettings
            connection={connection}
            onConnectionConfigValidationUpdate={setIsValidConfig}
            onTrackingUrlUpdate={handleOnTrackingUrlUpdate}
            trackingUrl={connection.config.trackingUrl}
          />
        )
      case 'shopify':
        return (
          <ShopifySettings
            connection={connection}
            onConnectionConfigValidationUpdate={setIsValidConfig}
            onShopUpdate={handleOnShopUpdate}
          />
        )
      default:
        return (
          <GenericSettings
            connection={connection}
            connectionConfig={connectionConfig}
            onConnectionConfigUpdate={handleConnectionConfigUpdate}
            onConnectionConfigValidationUpdate={setIsValidConfig}
          />
        )
    }
  }

  return (
    <>
      {renderConnectionSettings()}
      <div className={styles.actions}>
        {saveButtonState !== 'hidden' && (
          <Button
            disabled={saveButtonState === 'disabled'}
            loading={isSaving}
            onClick={(e) => {
              e.stopPropagation()

              void handleSave()
            }}
          >
            {t('setup.actions.save')}
          </Button>
        )}
      </div>
    </>
  )
}

export default ConnectionSettings
